import { Alert, AlertTitle, Button, Grid } from '@mui/material';
import * as yup from 'yup';
import { Box } from '@mui/system';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getInvoiceTemplateById, sendSendingsByMissionId } from 'src/shared/services/invoiceService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { useGlobals } from 'src/shared/contexts/appContext';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { InvoiceDeliveryMethodIntegerEnum } from 'src/shared/models/enums/invoiceDeliveryMethodInteger.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { SendSendingsByMissionIdRequest } from '../models/sendSendingsByMissionIdRequest';

type SendMemberInvoiceEmailProps = {
  modalOpen: boolean;
  onSave: () => void;
  onClose: () => void;
  invoiceMission: InvoiceMissionResponse;
  hideBackdrop?: boolean;
  sendingType: InvoiceSendingTypeIntegerEnum;
  navigateToInvoiceMission?: boolean;
};

type FormValues = {
  subject?: string;
  body?: string;
  missionId: number;
  sndDate: string;
  deliveryMethod: InvoiceDeliveryMethodIntegerEnum;
  replyEmail?: string;
  runAsTaskWithTaskService: boolean;
};

export const SendMemberInvoiceEmail = (props: SendMemberInvoiceEmailProps) => {
  const { modalOpen, onSave, onClose, invoiceMission, hideBackdrop = false, sendingType, navigateToInvoiceMission } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const [loading, setLoading] = useState<boolean>(false);
  const countNoEmail = invoiceMission.data.totalCount - invoiceMission.data.emailCount;
  const { setDateStamp, currentUser } = useGlobals();

  const isSuperAdmin = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  const InvoiceMailSenderSchema: yup.Schema<FormValues> = yup.object().shape({
    subject: yup.string().optional(),
    body: yup.string().optional(),
    sndDate: yup.string().required(),
    missionId: yup.number().required(),
    deliveryMethod: yup.number().required(),
    replyEmail: yup.string().email().optional(),
    runAsTaskWithTaskService: yup.boolean().required(),
  });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(InvoiceMailSenderSchema),
    mode: 'all',
    defaultValues: {
      subject: '',
      body: '',
      sndDate: DateHelper.getDate(new Date()),
      missionId: invoiceMission.invoiceMission_id,
      deliveryMethod: InvoiceDeliveryMethodIntegerEnum.mail,
      replyEmail: currentUser?.email,
      runAsTaskWithTaskService: false,
    },
  });

  const {
    handleSubmit,
    setValue,
    formState: { errors },
  } = formMethods;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getInvoiceTemplateById(invoiceMission.template_id ?? 0);
        if (isMounted()) {
          ConsoleHelper.log('res?.data TEMPLATE', res?.data);
          const sendingContent =
            res?.data.sendings?.find((sendingItem) => sendingItem.sendingType === sendingType) ??
            res?.data.sendings?.find((sendingItem) => sendingItem.sendingType === InvoiceSendingTypeIntegerEnum.Email);
          ConsoleHelper.log('sendingContent', sendingContent);
          setValue('subject', sendingContent?.emailHeading);
          setValue('body', sendingContent?.emailText);
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log(e);
      } finally {
        if (isMounted()) setLoading(false);
      }
    };

    fetchData();
  }, [isMounted, invoiceMission, sendingType, setValue]);

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    const dataToPost: SendSendingsByMissionIdRequest = {
      missionId: data.missionId,
      sndDate: data.sndDate,
      deliveryMethod: data.deliveryMethod,
      async: true,
      sendingType,
      replyEmail: data.replyEmail,
      runAsTaskWithTaskService: data.runAsTaskWithTaskService,
    };
    ConsoleHelper.log('dataToPost', dataToPost);
    try {
      await sendSendingsByMissionId(dataToPost);
      if (isMounted()) {
        setDateStamp(new Date());
        toast.success(<>{t('invoice:createMemberInvoice.invoicesSending')}</>);
        if (navigateToInvoiceMission) {
          navigate(`${RoutesEnum.InvoiceMissionDetailSendingType}/${data.missionId}/${InvoiceSendingTypeIntegerEnum.NotSet}`);
        }
        onClose();
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:invoiceMissionPage.failedSending')}</>);
      }
    } finally {
      setLoading(false);
    }
  };

  const getEmailAlertMessage = () => {
    if (invoiceMission.data.emailCount === 0) {
      return t('invoice:invoiceMissionPage.emailAlertAllMissingEmail');
    }
    if (invoiceMission.data.emailCount === invoiceMission.data.totalCount) {
      return t('invoice:invoiceMissionPage.emailAlertAllHaveEmail');
    }
    return t('invoice:invoiceMissionPage.emailAlertText', { countNoEmail });
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      disableBackdropClosing
      dialogTitle={t('invoice:createMemberInvoice.modalHeaderSendEmail')}
      onClose={onClose}
      onSave={onSave}
      showDialogActions
      hideBackdrop={hideBackdrop}
      dialogActions={
        <Box
          sx={{
            pr: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button type='submit' form='invoiceForm' disabled={loading || Object.keys(errors).length > 0} disableElevation variant='contained'>
            {t('invoice:createMemberInvoice.sendInvoices')}
          </Button>
        </Box>
      }
      dialogContent={
        <Box
          sx={{
            position: 'relative',
            minHeight: '22rem',
          }}
        >
          <LoadingOverlay loading={loading} />
          <FormProvider {...formMethods}>
            <GridWrapper>
              <Grid item xs={12}>
                <Alert severity='info'>
                  <AlertTitle>
                    {t('invoice:invoiceMissionPage.emailAlertTitle', {
                      emailCount: invoiceMission.data.emailCount,
                      totalCount: invoiceMission.data.totalCount,
                    })}
                  </AlertTitle>
                  {getEmailAlertMessage()}
                  <br />
                  <br />
                  {t('invoice:emailAlertAttachmentInfo')}
                </Alert>
              </Grid>
            </GridWrapper>
            <form id='invoiceForm' onSubmit={handleSubmit(onSubmit)}>
              <GridWrapper>
                <Grid item xs={12} mt={1.5}>
                  <Box sx={{ p: 2 }}>
                    <GridWrapper>
                      {isSuperAdmin && (
                        <Grid item xs={12}>
                          <ControlledCheckboxNew name='runAsTaskWithTaskService' label={t('invoice:runAsTask')} />
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <ControlledTextField name='subject' label={t('invoice:createInvoice.invoiceRow.heading')} disabled />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextEditor rows={30} name='body' disabled />
                      </Grid>
                      <Grid item xs={12} sx={{ mt: { xs: 1, md: 0 } }}>
                        <ControlledTextField
                          name='replyEmail'
                          label={t('invoice:invoiceMissionPage.sender')}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </Grid>
                    </GridWrapper>
                  </Box>
                </Grid>
              </GridWrapper>
            </form>
          </FormProvider>
        </Box>
      }
    />
  );
};
